import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { Calendar, ChevronLeft, ChevronRight, Clock, Briefcase, FileText } from 'lucide-react';

const DAYS_SE = ['Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag', 'Söndag'];
const MONTH_SE = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];

const fmtDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const startOfWeek = (d) => {
  const x = new Date(d);
  const wd = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - wd);
  x.setHours(0, 0, 0, 0);
  return x;
};

const WorkerSchedule = () => {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  }), [weekStart]);

  const fromDate = fmtDate(weekDays[0]);
  const toDate = fmtDate(weekDays[6]);

  useEffect(() => {
    setLoading(true);
    api.listMyShifts({ from_date: fromDate, to_date: toDate })
      .then(res => setShifts(res.data || []))
      .catch(() => toast.error('Kunde inte hämta schema'))
      .finally(() => setLoading(false));
  }, [fromDate, toDate]);

  const byDate = useMemo(() => {
    const map = {};
    shifts.forEach(s => {
      if (!map[s.date]) map[s.date] = [];
      map[s.date].push(s);
    });
    return map;
  }, [shifts]);

  const weekLabel = `${weekDays[0].getDate()} ${MONTH_SE[weekDays[0].getMonth()]} – ${weekDays[6].getDate()} ${MONTH_SE[weekDays[6].getMonth()]}`;
  const totalHours = useMemo(() => {
    let h = 0;
    shifts.forEach(s => {
      const [sh, sm] = s.start_time.split(':').map(Number);
      const [eh, em] = s.end_time.split(':').map(Number);
      h += (eh + em / 60) - (sh + sm / 60);
    });
    return Math.round(h * 10) / 10;
  }, [shifts]);

  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="worker-schedule-page">
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Calendar size={20} className="text-[#E8613A]" /> Mitt schema
        </h1>
        <p className="text-xs text-muted-foreground mt-1">Dina kommande pass</p>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={() => setWeekStart(new Date(weekStart.getTime() - 7 * 86400000))}
          className="p-2 rounded-md border border-border hover:bg-muted active:scale-95" data-testid="ws-prev">
          <ChevronLeft size={14} />
        </button>
        <button onClick={() => setWeekStart(startOfWeek(new Date()))}
          className="text-xs px-3 h-9 rounded-md border border-border hover:bg-muted font-medium flex-1" data-testid="ws-today">
          Denna vecka
        </button>
        <button onClick={() => setWeekStart(new Date(weekStart.getTime() + 7 * 86400000))}
          className="p-2 rounded-md border border-border hover:bg-muted active:scale-95" data-testid="ws-next">
          <ChevronRight size={14} />
        </button>
      </div>

      <div className="bg-[#1A1A1A] text-white rounded-xl p-4 flex items-center justify-between">
        <div>
          <p className="text-xs opacity-70 uppercase tracking-wider">{weekLabel}</p>
          <p className="text-lg font-bold mt-0.5">{shifts.length} pass · {totalHours} timmar</p>
        </div>
        <Clock size={28} className="text-[#E8613A]" />
      </div>

      {loading ? (
        <p className="text-center text-sm text-muted-foreground py-6">Laddar…</p>
      ) : shifts.length === 0 ? (
        <div className="bg-card border-2 border-dashed border-border rounded-xl p-8 text-center" data-testid="ws-empty">
          <Calendar size={32} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Inga schemalagda pass denna vecka</p>
        </div>
      ) : (
        <div className="space-y-2">
          {weekDays.map((d, i) => {
            const dateStr = fmtDate(d);
            const dayShifts = byDate[dateStr] || [];
            if (dayShifts.length === 0) return null;
            const isToday = dateStr === fmtDate(new Date());
            return (
              <div key={dateStr} className="bg-card border border-border rounded-xl overflow-hidden" data-testid={`ws-day-${dateStr}`}>
                <div className={`px-4 py-2 ${isToday ? 'bg-[#E8613A] text-white' : 'bg-muted/50'}`}>
                  <p className={`text-xs uppercase tracking-wider font-semibold ${isToday ? 'text-white/90' : 'text-muted-foreground'}`}>
                    {DAYS_SE[i]} {d.getDate()} {MONTH_SE[d.getMonth()]} {isToday && ' · Idag'}
                  </p>
                </div>
                <div className="divide-y divide-border">
                  {dayShifts.map(s => (
                    <div key={s.id} className="px-4 py-3" data-testid={`ws-shift-${s.id}`}>
                      <div className="flex items-center justify-between">
                        <p className="font-bold text-foreground text-base">{s.start_time}–{s.end_time}</p>
                        <p className="text-xs font-medium text-[#E8613A]">
                          {Math.round(((parseInt(s.end_time.split(':')[0]) + parseInt(s.end_time.split(':')[1]) / 60) - (parseInt(s.start_time.split(':')[0]) + parseInt(s.start_time.split(':')[1]) / 60)) * 10) / 10} h
                        </p>
                      </div>
                      {s.title && (
                        <p className="text-sm font-medium mt-1.5">{s.title}</p>
                      )}
                      {s.project_name && (
                        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                          <Briefcase size={11} /> {s.project_name}
                        </p>
                      )}
                      {s.notes && (
                        <p className="text-xs text-muted-foreground mt-1.5 flex items-start gap-1">
                          <FileText size={11} className="mt-0.5" /> {s.notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WorkerSchedule;
